import React from 'react';

import { Box, Chip, CircularProgress, Paper, Stack } from '@mui/material';

import type { AccountIndexSchema } from '@liscio/api';
import { Typography } from '@liscio/ui';

import { HighlightSearchMatch } from 'components/CreateContactForm/HighlightSearchMatch';

export type AccountSuggestionPanelProps = {
  nameQuery: string;
  suggestions: AccountIndexSchema[];
  isLoading: boolean;
  onSelectExisting: (account: AccountIndexSchema) => void;
};

const SuggestionRow: React.FC<{
  account: AccountIndexSchema;
  searchQuery: string;
  onAction: () => void;
}> = ({ account, searchQuery, onAction }) => (
  <Box
    component="button"
    type="button"
    onClick={onAction}
    onMouseDown={(event) => event.preventDefault()}
    aria-label={`Existing account ${account.name}`}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      width: '100%',
      px: 2,
      py: 1.5,
      border: 'none',
      bgcolor: 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
      font: 'inherit',
      color: 'inherit',
      '&:hover': {
        bgcolor: 'action.hover',
      },
      '&:not(:last-of-type)': {
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    }}
  >
    <Stack flex={1} minWidth={0} spacing={0.25}>
      <HighlightSearchMatch
        content={account.name}
        query={searchQuery}
        variant="body2"
        fontWeight={700}
        noWrap
      />
    </Stack>

    <Chip
      label="Already in Liscio"
      size="small"
      color="success"
      sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }}
    />
  </Box>
);

export const AccountSuggestionPanel: React.FC<AccountSuggestionPanelProps> = ({
  nameQuery,
  suggestions,
  isLoading,
  onSelectExisting,
}) => {
  const trimmedName = nameQuery.trim();
  const hasResults = suggestions.length > 0;

  if (!isLoading && !hasResults) {
    return null;
  }

  return (
    <Paper
      elevation={4}
      sx={{
        mt: 1,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {isLoading && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Searching accounts…
          </Typography>
        </Stack>
      )}

      {!isLoading && hasResults && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              px: 2,
              pt: 1.5,
              pb: 0.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Existing accounts
          </Typography>
          {suggestions.map((account) => (
            <SuggestionRow
              key={account.uuid}
              account={account}
              searchQuery={trimmedName}
              onAction={() => onSelectExisting(account)}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};
